import React, { useState } from 'react';
import { doctoralApi } from '@/features/workspace/api';
import { DoctoralPracticeData } from '../types';
import { OFFICIAL, PRACTICE_VIDEOS } from '../doctoralTemplate';
import { Card, DocRow, Hero, SourceCard } from './dp120Parts';

interface DoctoralPracticesViewProps {
  practices?: DoctoralPracticeData[];
  onUploadDoc?: (practiceType: string, docType: string, file: File) => void;
}

const TEACHING = [
  ['schedule_file', 'План-график'],
  ['order_file', 'Приказ / направление'],
  ['diary_file', 'Дневник-отчёт'],
  ['referral_file', 'Завершение'],
] as const;

const RESEARCH = [
  ['schedule_file', 'План-график'],
  ['order_file', 'Приказ'],
  ['diary_file', 'Дневник-отчёт'],
] as const;

export const DoctoralPracticesView: React.FC<DoctoralPracticesViewProps> = ({ practices = [], onUploadDoc }) => {
  const [kind, setKind] = useState<'PEDAGOGICAL' | 'RESEARCH'>('PEDAGOGICAL');
  const [notice, setNotice] = useState('');
  const teaching = kind === 'PEDAGOGICAL';
  const current = practices.find((item) => item.practice_type === kind);
  const docs = teaching ? TEACHING : RESEARCH;

  const upload = async (docType: string, file: File) => {
    setNotice('');
    onUploadDoc?.(kind, docType, file);
    try {
      await doctoralApi.updatePractice({
        practice_type: kind,
        status: current?.status || 'IN_PREPARATION',
        stage_step: current?.stage_step || 1,
        [docType]: file.name,
      });
      setNotice(`Файл «${file.name}» сохранён.`);
    } catch {
      setNotice('Не удалось сохранить файл практики.');
    }
  };

  return (
    <>
      <Hero
        kicker="PRACTICE · SMART.ENU"
        title="Практики докторанта"
        text="Педагогическая и исследовательская практика находятся в одном разделе, но каждая имеет собственный комплект документов и статус."
        color="#4ce2bf"
      />
      {notice ? <div className="dp120-callout" style={{ marginTop: 10 }}><b>{notice}</b></div> : null}
      <div className="dp120-subtabs">
        <button type="button" className={teaching ? 'active' : ''} onClick={() => setKind('PEDAGOGICAL')}>
          Педагогическая практика
        </button>
        <button type="button" className={!teaching ? 'active' : ''} onClick={() => setKind('RESEARCH')}>
          Исследовательская практика
        </button>
        <a className="dp120-mini mint" href={OFFICIAL.smart} target="_blank" rel="noopener noreferrer">
          Smart.ENU ↗
        </a>
      </div>
      <div className="dp120-grid">
        <Card
          title={teaching ? 'Педагогическая практика' : 'Исследовательская практика'}
          sub={
            teaching
              ? 'План-график → приказ/направление → дневник-отчёт → завершение.'
              : 'План-график → база исследования → приказ → дневник-отчёт → защита отчёта.'
          }
          span="span8"
        >
          <div className="dp120-list">
            {docs.map(([key, title]) => (
              <DocRow
                key={key}
                title={title}
                file={current?.[key]}
                onPick={(file) => upload(key, file)}
              />
            ))}
          </div>
        </Card>
        <Card title="Маршрут" sub="Фактические даты берутся из ИУП и академического календаря." span="span4">
          <div className="dp120-flow" style={{ flexDirection: 'column' }}>
            {[
              ['01', 'Подготовка', '#42d8ff'],
              ['02', 'Оформление', '#f1b93f'],
              ['03', 'Прохождение', '#4ce2bf'],
              ['04', 'Отчёт / защита', '#9b7cff'],
            ].map(([num, title, color]) => (
              <div key={num} className="dp120-flow-step" style={{ ['--s' as string]: color }}>
                <b>{num}</b>
                <strong>{title}</strong>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Smart.ENU · инструкции" sub="Инструкции находятся рядом с процессом." span="span8">
          <div className="dp120-list">
            {PRACTICE_VIDEOS.map(([title, href], index) => (
              <div key={href} className="dp120-row">
                <span>
                  <strong>
                    {index + 1}. {title}
                  </strong>
                </span>
                <a className="dp120-mini" href={href} target="_blank" rel="noopener noreferrer">
                  Открыть ↗
                </a>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Официальная информация ENU" span="span4">
          <SourceCard
            title="Практика ENU"
            text="Электронное оформление договоров по практике проходит через Smart.ENU. Открывайте актуальную редакцию."
            links={[
              ['Практика ENU', OFFICIAL.practice],
              ['Smart.ENU', OFFICIAL.smart],
            ]}
          />
        </Card>
      </div>
    </>
  );
};
